import type { PlanElement } from '../services/api';

/**
 * Time Calculations for Production Plans
 * 
 * Pure functions for calculating event times, durations, and formatting.
 * Extracted from PlanEditor.tsx for reusability and testability.
 */

/**
 * Duration type
 */
export interface Duration {
  hours: number;
  minutes: number;
  seconds: number;
}

/**
 * Time calculation options
 */
export interface TimeCalculationOptions {
  produksjonStart: Date;
  elements: PlanElement[];
}

/**
 * Beregner total varighet for alle hendelser i planen
 * 
 * @param elements - Plan-elementer
 * @returns Varighet i timer, minutter og sekunder
 */
export const calculateTotalDuration = (elements: PlanElement[]): Duration => {
  const totalMinutes = elements
    .filter(el => el.type === 'hendelse' && el.varighet_minutter)
    .reduce((sum, el) => sum + (el.varighet_minutter || 0), 0);
  
  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.floor(totalMinutes % 60);
  const seconds = Math.floor((totalMinutes % 1) * 60);
  
  return { hours, minutes, seconds };
};

/**
 * Formaterer varighet til lesbar string (f.eks. "2t 30m 15s")
 * 
 * @param duration - Varighet
 * @returns Formatert string
 */
export const formatDuration = (duration: Duration): string => {
  const parts: string[] = [];
  if (duration.hours > 0) parts.push(`${duration.hours}t`);
  if (duration.minutes > 0 || duration.hours > 0) parts.push(`${duration.minutes}m`);
  parts.push(`${duration.seconds}s`);
  return parts.join(' ');
};

/**
 * Formaterer klokkeslett til norsk format (HH:MM)
 * 
 * @param date - Dato/tid
 * @returns Formatert string (f.eks. "14:30")
 */
export const formatTime = (date: Date): string => {
  return date.toLocaleTimeString('nb-NO', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false 
  });
};

/**
 * Formaterer varighet til kort format (MM:SS)
 * 
 * @param minutter - Varighet i minutter (kan ha desimaler for sekunder)
 * @returns Formatert string (f.eks. "05:30")
 */
export const formatDurationShort = (minutter: number): string => {
  const minutes = Math.floor(minutter);
  const seconds = Math.floor((minutter % 1) * 60);
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

/**
 * Helper: Beregn starttid for hendelser FØR produksjonsstart
 * 
 * Hendelser under overskrifter markert som "før_produksjonsstart" telles bakover fra produksjonsstart.
 * Eksempel: Produksjon kl 19:00, hendelse "Lydsjekk" (30 min) vises kl 18:30-19:00
 * 
 * @param hendelser - Liste over hendelser under overskriften
 * @param elementId - ID på hendelsen vi skal finne starttid for
 * @param prodStart - Produksjonens starttid
 * @returns Starttid for hendelsen, eller null hvis ikke funnet
 */
const calculateBeforeProdStart = (
  hendelser: PlanElement[], 
  elementId: number, 
  prodStart: Date
): Date | null => {
  const totalDuration = hendelser.reduce((sum, h) => sum + (h.varighet_minutter || 0), 0);
  let offsetMinutes = -totalDuration;
  
  for (const hendelse of hendelser) {
    if (hendelse.id === elementId) {
      return new Date(prodStart.getTime() + offsetMinutes * 60000);
    }
    offsetMinutes += hendelse.varighet_minutter || 0;
  }
  return null;
};

/**
 * Helper: Beregn starttid for hendelser ETTER produksjonsstart
 * 
 * Hendelser under vanlige overskrifter telles fremover fra produksjonsstart + kumulative minutter.
 * 
 * @param hendelser - Liste over hendelser under overskriften
 * @param elementId - ID på hendelsen vi skal finne starttid for
 * @param prodStart - Produksjonens starttid
 * @param cumulativeMinutes - Kumulative minutter fra tidligere hendelser
 * @returns Starttid for hendelsen, eller null hvis ikke funnet
 */
const calculateAfterProdStart = (
  hendelser: PlanElement[], 
  elementId: number, 
  prodStart: Date,
  cumulativeMinutes: number
): Date | null => {
  let cumulative = cumulativeMinutes;
  for (const hendelse of hendelser) {
    if (hendelse.id === elementId) {
      return new Date(prodStart.getTime() + cumulative * 60000);
    }
    cumulative += hendelse.varighet_minutter || 0;
  }
  return null;
};

/**
 * Beregner starttid for en hendelse basert på produksjonens starttid og alle tidligere hendelser
 * 
 * Tar hensyn til:
 * - Overskrifter markert som "før_produksjonsstart" (telles bakover)
 * - Normale overskrifter (telles fremover)
 * - Rekkefølge av overskrifter og hendelser
 * - Kumulative varigheter
 * 
 * @param elementId - ID på hendelsen
 * @param options - Beregningsopsjoner (produksjonsstart og plan-elementer)
 * @returns Starttid for hendelsen, eller null hvis ikke funnet eller ugyldig input
 */
export const calculateStartTime = (
  elementId: number,
  options: TimeCalculationOptions
): Date | null => {
  const { produksjonStart, elements } = options;
  
  if (!produksjonStart) return null;
  
  const prodStart = new Date(produksjonStart);
  const overskrifter = elements
    .filter(el => el.type === 'overskrift')
    .sort((a, b) => a.rekkefølge - b.rekkefølge);
  
  let cumulativeMinutes = 0;
  
  for (const overskrift of overskrifter) {
    const hendelser = elements
      .filter(el => el.parent_id === overskrift.id)
      .sort((a, b) => a.rekkefølge - b.rekkefølge);
    
    if (overskrift.før_produksjonsstart) {
      const result = calculateBeforeProdStart(hendelser, elementId, prodStart);
      if (result) return result;
    } else {
      const result = calculateAfterProdStart(hendelser, elementId, prodStart, cumulativeMinutes);
      if (result) return result;
      cumulativeMinutes += hendelser.reduce((sum, h) => sum + (h.varighet_minutter || 0), 0);
    }
  }
  
  return null;
};

